import { useEffect, useState } from "react";

type Options = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
};

/**
 * Observe many DOM elements by id; returns true if ANY of them is in view.
 * - Handles late-mounted/removed elements via MutationObserver.
 * - Safe for SSR (no-op until client).
 */
export function useAnyIdInView(ids: string[], opts?: Options) {
  const [anyInView, setAnyInView] = useState(false);
  const idsKey = ids.join("\u0000");
  const root = opts?.root ?? null;
  const rootMargin = opts?.rootMargin ?? "0px 0px -10% 0px";
  const thresholdKey = Array.isArray(opts?.threshold)
    ? opts.threshold.join(",")
    : String(opts?.threshold ?? 0.25);

  useEffect(() => {
    const watchedIds = idsKey ? idsKey.split("\u0000") : [];
    const threshold = thresholdKey.includes(",")
      ? thresholdKey.split(",").map(Number)
      : Number(thresholdKey);

    if (typeof window === "undefined") return; // SSR guard
    if (!watchedIds.length) {
      setAnyInView(false);
      return;
    }

    // Local state for this effect
    const visMap = new Map<HTMLElement, boolean>();
    const observed = new Set<HTMLElement>();

    const updateAny = () => {
      setAnyInView(Array.from(visMap.values()).some(Boolean));
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          visMap.set(e.target as HTMLElement, e.isIntersecting);
        }
        updateAny();
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    const getDesiredElements = (): HTMLElement[] =>
      watchedIds
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el instanceof HTMLElement);

    const syncObserved = () => {
      const desired = new Set(getDesiredElements());

      // Unobserve elements no longer present
      for (const el of observed) {
        if (!desired.has(el)) {
          io.unobserve(el);
          observed.delete(el);
          visMap.delete(el);
        }
      }

      // Observe new elements
      for (const el of desired) {
        if (!observed.has(el)) {
          observed.add(el);
          visMap.set(el, false);
          io.observe(el);
        }
      }

      // If nothing is observed, ensure state reflects that
      if (observed.size === 0) {
        setAnyInView(false);
      } else {
        // kick an immediate compute (in case IO callback hasn’t fired yet)
        requestAnimationFrame(updateAny);
      }
    };

    // Initial attach
    syncObserved();

    // Watch for nodes being added/removed anywhere in the document
    const mo = new MutationObserver(() => {
      // Re-scan and (un)observe accordingly
      syncObserved();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Also re-sync on viewport resizes (some UIs gate-mount on responsive changes)
    const onResize = () => syncObserved();
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      mo.disconnect();
      window.removeEventListener("resize", onResize);
      io.disconnect();
      observed.clear();
      visMap.clear();
    };
  }, [idsKey, root, rootMargin, thresholdKey]);

  return anyInView;
}
